import { type RefObject, useEffect } from 'react';
import { appContext } from '../../app/context';
import { applyStrokeToLayer, ensurePaintTarget, type PaintTarget } from '../../app/paintTarget';
import { layerEditStale, STALE_EDIT_MESSAGE } from '../../app/staleGuard';
import { isEditableTarget } from '../../core/input/shortcuts';
import type { BrushSettings, StrokePoint } from '../../core/paint/stroke';
import { strokeStamps } from '../../core/paint/stroke';
import {
  bakeStroke,
  MASK_VEIL,
  MaskVeilPreview,
  type StampRenderer,
  StrokePreview,
  type StrokePreviewSurface,
} from '../../io/paintStroke';
import { useEditorStore } from '../../state/editorStore';
import { clientToDoc } from '../pointer';

/**
 * Painting on the stage.
 *
 * The rule that shapes this file: **one stroke, one history entry**. Pointer
 * samples accumulate into a session, the preview shows the live mask, and only
 * `pointerup` bakes pixels and commits. A 200-sample stroke is one Ctrl+Z.
 *
 * Painting a *layer mask* goes down the same path with two differences: the ink is
 * coverage rather than the foreground colour, and the preview is a veil showing
 * what the stroke hides (see {@link MaskVeilPreview}).
 */

const PAINT_TOOLS = new Set(['brush', 'pencil', 'eraser']);

interface Session {
  target: PaintTarget;
  settings: BrushSettings;
  renderer: StampRenderer;
  erase: boolean;
  label: string;
  points: StrokePoint[];
  preview: StrokePreviewSurface;
  element: HTMLCanvasElement;
  drawn: number;
}

export function usePainting(
  stageRef: RefObject<HTMLDivElement | null>,
  previewHostRef: RefObject<HTMLDivElement | null>,
): void {
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    let session: Session | null = null;

    const settingsFor = (
      onMask: boolean,
    ): {
      settings: BrushSettings;
      renderer: StampRenderer;
      erase: boolean;
      label: string;
    } => {
      const { activeTool, paint, color } = useEditorStore.getState();
      // A mask is coverage, not colour: the ink is always opaque, whatever the
      // swatch says. White keeps a mask thumbnail readable.
      const ink = onMask ? '#ffffff' : color;
      if (activeTool === 'pencil') {
        // The pencil is deliberately exact: no smoothing, no soft edge.
        return {
          settings: {
            size: Math.max(1, paint.size),
            hardness: 1,
            opacity: 1,
            smoothing: 0,
            spacing: 0.4,
          },
          renderer: { color: ink, size: Math.max(1, paint.size), hardness: 1 },
          erase: false,
          label: onMask ? 'Paint Mask' : 'Pencil Stroke',
        };
      }
      if (activeTool === 'eraser') {
        return {
          settings: { ...paint, spacing: 0.15 },
          renderer: { color: null, size: paint.size, hardness: paint.hardness },
          erase: true,
          label: onMask ? 'Erase Mask' : 'Erase',
        };
      }
      return {
        settings: { ...paint, spacing: 0.15 },
        renderer: { color: ink, size: paint.size, hardness: paint.hardness },
        erase: false,
        label: onMask ? 'Paint Mask' : 'Brush Stroke',
      };
    };

    const layerPoint = (event: PointerEvent, target: PaintTarget): StrokePoint => {
      const viewport = useEditorStore.getState().viewport;
      const doc = clientToDoc(event.clientX, event.clientY, stage, viewport);
      const pressure = event.pointerType === 'pen' ? event.pressure : 1;
      return {
        x: (doc.x - target.x) / target.scaleX,
        y: (doc.y - target.y) / target.scaleY,
        pressure: pressure > 0 ? pressure : 1,
      };
    };

    const positionPreview = (element: HTMLCanvasElement, target: PaintTarget) => {
      const { viewport } = useEditorStore.getState();
      element.style.position = 'absolute';
      element.style.left = `${target.x * viewport.zoom + viewport.pan.x}px`;
      element.style.top = `${target.y * viewport.zoom + viewport.pan.y}px`;
      element.style.width = `${target.width * target.scaleX * viewport.zoom}px`;
      element.style.height = `${target.height * target.scaleY * viewport.zoom}px`;
      element.style.pointerEvents = 'none';
    };

    const repaint = () => {
      if (!session) return;
      const stamps = strokeStamps(session.points, session.settings);
      session.preview.append(stamps.slice(session.drawn));
      session.drawn = stamps.length;
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (!PAINT_TOOLS.has(useEditorStore.getState().activeTool)) return;
      // A second pointer (pen + touch, palm contact) must not orphan the stroke
      // already in flight — its preview would leak and its points would be lost.
      if (session) return;

      const target = ensurePaintTarget(appContext);
      if (!target) return;
      const onMask = target.field === 'mask';

      // `preventDefault` below stops the browser moving focus out of whatever
      // field the user was in — so picking a colour and then drawing would leave
      // focus in the colour input and swallow every keyboard shortcut. Move
      // focus out explicitly.
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && isEditableTarget(focused)) focused.blur();

      const { settings, renderer, erase, label } = settingsFor(onMask);
      const element = document.createElement('canvas');
      element.dataset.testid = 'stroke-preview';
      // On a mask the preview shows what the stroke *hides*, so its dabs are veil
      // coloured even though the ink being baked is white.
      let preview: StrokePreviewSurface;
      if (onMask) {
        const veil = new MaskVeilPreview(
          target.width,
          target.height,
          { ...renderer, color: MASK_VEIL },
          settings.opacity,
          erase,
        );
        veil.setStencil(target.src);
        preview = veil;
      } else {
        const flat = new StrokePreview(
          target.width,
          target.height,
          renderer,
          settings.opacity,
          erase,
        );
        flat.setBase(target.src);
        preview = flat;
      }
      positionPreview(element, target);
      previewHostRef.current?.appendChild(element);

      session = {
        target,
        settings,
        renderer,
        erase,
        label,
        points: [layerPoint(event, target)],
        preview,
        element,
        drawn: 0,
      };
      repaint();
      event.preventDefault();
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!session) return;
      session.points.push(layerPoint(event, session.target));
      repaint();
    };

    const finish = async () => {
      if (!session) return;
      const current = session;
      session = null;

      const stamps = strokeStamps(current.points, current.settings);
      try {
        const pixels = await bakeStroke({
          src: current.target.src,
          width: current.target.width,
          height: current.target.height,
          stamps,
          renderer: current.renderer,
          opacity: current.settings.opacity,
          erase: current.erase,
        });
        const store = useEditorStore.getState();
        // The bake read the layer as it stood at pointerdown; an undo (or any
        // commit) that touched this layer inside the window must not be
        // clobbered by the stroke landing on top of it.
        if (layerEditStale(appContext, current.target)) {
          appContext.notify(STALE_EDIT_MESSAGE, 'warn');
          return;
        }
        store.commit(current.label, applyStrokeToLayer(store.doc, current.target, pixels));
      } catch (error) {
        appContext.notify(
          error instanceof Error ? error.message : 'That stroke could not be painted.',
          'error',
        );
      } finally {
        current.preview.destroy();
        current.element.remove();
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      if (!session) return;
      session.points.push(layerPoint(event, session.target));
      void finish();
    };

    // A cancelled gesture (touch scroll takeover, palm rejection) commits
    // nothing and leaves nothing behind — same teardown as the cleanup below.
    const discard = () => {
      if (!session) return;
      const current = session;
      session = null;
      current.preview.destroy();
      current.element.remove();
    };
    const onPointerCancel = () => discard();

    stage.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);

    return () => {
      stage.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      discard();
    };
  }, [stageRef, previewHostRef]);
}
